"use client";

import React, { useState, useEffect } from "react";

export default function BuyCustomDomain({ userId, userEmail }) {
  const [domain, setDomain] = useState("");
  const [availability, setAvailability] = useState(null); // "available", "taken", or null
  const [price, setPrice] = useState(null); // registration_price from DNSimple (annual value used as key)
  const [alias, setAlias] = useState("");
  const [forwardTo, setForwardTo] = useState(""); // Optional: for email forwarding
  const [checking, setChecking] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false);

  // Monthly pricing mapping:
  // The key is the annual price from DNSimple (as string), and the value is an object containing:
  // - stripePriceId: the ID for monthly billing on Stripe
  // - monthly: the monthly price to display
  const monthlyPriceMapping = {
    "9.99":  { stripePriceId: "price_1R8AdWK5A174OlbcrBWnLJgZ", monthly: "5.99" },
    "19.99": { stripePriceId: "price_1R8AdWK5A174Olbc6zaDY5Vi", monthly: "6.99" },
    "29.99": { stripePriceId: "price_1R8AdWK5A174OlbcMJRFcz5q", monthly: "7.99" },
    "39.99": { stripePriceId: "price_1R8AdWK5A174Olbcwco7cb4F", monthly: "8.99" },
    "49.99": { stripePriceId: "price_1R8AdWK5A174OlbcBwQPuQcI", monthly: "9.99" },
    "59.99": { stripePriceId: "price_1R8AdWK5A174OlbcnPupCXcd", monthly: "10.99" },
    "69.99": { stripePriceId: "price_1R8AdWK5A174Olbc5U6RPihp", monthly: "11.99" },
    "79.99": { stripePriceId: "price_1R8AdWK5A174OlbckYOvAwjZ", monthly: "12.99" },
    "89.99": { stripePriceId: "price_1R8AdWK5A174Olbcv6Sh5dY9", monthly: "13.99" },
    "99.99": { stripePriceId: "price_1R8AdWK5A174OlbcornT0xOz", monthly: "14.99" },
    "109.99": { stripePriceId: "price_1R8AdWK5A174OlbcLVrjEhft", monthly: "15.99" },
    "119.99": { stripePriceId: "price_1R8AdWK5A174OlbcQmzR8ptg", monthly: "16.99" },
    "129.99": { stripePriceId: "price_1R8AdWK5A174OlbcflKY0lUc", monthly: "17.99" },
    "139.99": { stripePriceId: "price_1R8AdWK5A174Olbcw6WfgncO", monthly: "18.99" },
    "149.99": { stripePriceId: "price_1R8AdWK5A174OlbctW8YPDBD", monthly: "19.99" },
  };

  // Debounce the domain input to trigger API calls only after the user stops typing
  useEffect(() => {
    if (!domain || !domain.includes(".")) {
      setAvailability(null);
      setPrice(null);
      return;
    }
    const timeoutId = setTimeout(() => {
      setChecking(true);
      checkDomainAvailability(domain);
      fetchDomainPrice(domain);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [domain]);

  // Check domain availability via your API route
  async function checkDomainAvailability(domainName) {
    try {
      const res = await fetch(`/api/checkDomain?domain=${encodeURIComponent(domainName)}`);
      const data = await res.json();
      if (data?.data?.available) {
        setAvailability("available");
      } else {
        setAvailability("taken");
      }
    } catch (error) {
      console.error("Error checking domain:", error);
      setAvailability(null);
    } finally {
      setChecking(false);
    }
  }

  // Fetch domain pricing using the registration_price field from DNSimple
  async function fetchDomainPrice(domainName) {
    try {
      const res = await fetch(`/api/domainPrices?domain=${encodeURIComponent(domainName)}`);
      const data = await res.json();
      if (data?.data) {
        setPrice(data.data.registration_price || null);
      }
    } catch (error) {
      console.error("Error fetching price:", error);
      setPrice(null);
    }
  }

  // Initiate the Stripe Checkout process, including user details, alias, etc.
  async function handleCheckout() {
    if (!domain || !price) {
      alert("Please enter a valid domain and wait for pricing to load.");
      return;
    }
    // Use the annual price from DNSimple as a key to lookup monthly pricing details
    const priceKey = Number(price).toFixed(2); // e.g. "29.99"
    const mapping = monthlyPriceMapping[priceKey];
    if (!mapping) {
      alert("No matching price available for this domain.");
      return;
    }
    setLoadingCheckout(true);
    try {
      const response = await fetch("/api/createCheckoutSession", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          domain,
          selectedDomainPriceId: mapping.stripePriceId,
          // Pass along user details from props and the alias from the form
          userId,
          userEmail,
          alias,
        }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.assign(data.url);
      } else {
        console.error("Checkout session creation failed", data);
      }
    } catch (error) {
      console.error("Error during checkout:", error);
    } finally {
      setLoadingCheckout(false);
    }
  }

  return (
    <div className="w-full p-6 bg-gray-800 rounded-md shadow-md text-white">
      <h2 className="text-xl font-bold mb-4">Find a unique domain name:</h2>
      {/* Domain Input */}
      <div className="relative mb-4">
        <input
          type="text"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="yourcompanyname.com"
          className="w-full p-3 pr-20 rounded-md bg-gray-700 text-white placeholder-gray-400 focus:outline-none"
        />
        {checking && (
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-400">
            Checking...
          </span>
        )}
        {availability && !checking && (
          <span
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-sm font-semibold ${
              availability === "available" ? "text-green-500" : "text-red-500"
            }`}
          >
            {availability === "available" ? "Available" : "Taken"}
          </span>
        )}
      </div>

      {/* Alias Field (shown only if domain is available) */}
      {availability === "available" && (
        <>
          <div className="mb-4">
            <label className="block mb-1">Create your email alias</label>
            <div className="flex">
              <input
                type="text"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder="yourname"
                className="flex-1 p-2 rounded-l-md bg-gray-700 text-white focus:outline-none"
              />
              <span className="inline-flex items-center px-3 rounded-r-md bg-gray-600">
                @{domain}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              You will be able to create up to 100 aliases associated with your unique domain after checkout.
            </p>
          </div>

          {/* Single Price Display */}
          <div className="border-t border-gray-600 pt-4 mb-4">
            {monthlyPriceMapping[Number(price).toFixed(2)] ? (
              <>
                <div className="mb-2">
                  <span className="font-semibold">Odyssey Premium + {domain}:</span>{" "}
                  ${monthlyPriceMapping[Number(price).toFixed(2)].monthly}/Month
                </div>
                <p className="text-xs text-gray-400">
                  Also includes one masking phone number that forwards to your regular number.
                </p>
              </>
            ) : (
              <div className="mb-2">Loading pricing...</div>
            )}
          </div>

          <button
            onClick={handleCheckout}
            disabled={loadingCheckout}
            className="w-full bg-indigo-600 hover:bg-indigo-700 p-3 rounded-md font-bold"
          >
            {loadingCheckout ? "Processing..." : "Checkout"}
          </button>
        </>
      )}
    </div>
  );
}